/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { jsx } from '@compiled/react';
// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
import uuid from 'uuid/v4';

import {
	type Nudge,
	useAINudges,
	useAISurfaceMetadata,
	useViewAIActions,
} from '@atlassian/ai-smart-button-api';

// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import { useRovoButtonEventsInstrumentation } from '../../../../../hooks/useRovoButtonEventsInstrumentation';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import { isBrowser } from '../../../../common/utils/is-browser';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import { useFetchNudgeRecommendations } from '../../../hooks/useFetchNudgeRecommendations';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import { NudgeContent } from '../../nudge-content';

const NUDGE_DISMISS_TIMEOUT = 30000;
const ANIMATION_DURATION = 200;

export const AIProactiveNudge = () => {
	const [dismissedIds, setDismissedIds] = useState<Record<string, boolean>>({});
	const [acceptedIds, setAcceptedIds] = useState<Record<string, boolean>>({});

	// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
	const [triggerId] = useState(() => uuid());
	const [isNudgeVisible, setIsNudgeVisible] = useState(false);
	const [prevNudgeId, setPrevNudgeId] = useState<string | null>(null);
	const [isMouseHovering, setIsMouseHovering] = useState<boolean>(false);
	const hoverTimerRef = useRef<NodeJS.Timeout | null>(null);

	const registeredNudges = useAINudges();
	const surfaceMetadata = useAISurfaceMetadata();
	const { fireRovoButtonUIEvent } = useRovoButtonEventsInstrumentation();
	const { changeView } = useViewAIActions();
	const { recommendations, loading, error } = useFetchNudgeRecommendations(
		triggerId,
		isBrowser() ? window.location.href : '',
		surfaceMetadata?.product,
	);

	const activeNudge: Nudge | null = useMemo(() => {
		if (loading || error || !recommendations) {
			return null;
		}

		/**
		 * Nudge must not be previously dismissed or accepted
		 * to be considered active.
		 */
		if (dismissedIds[recommendations.nudge_id] || acceptedIds[recommendations.nudge_id]) {
			return null;
		}

		return (
			registeredNudges.find(
				(nudge: Nudge) => nudge.id === recommendations.feature_details.feature_id,
			) || null
		);
	}, [loading, error, recommendations, dismissedIds, acceptedIds, registeredNudges]);

	useEffect(() => {
		if (activeNudge && recommendations && recommendations.nudge_id !== prevNudgeId) {
			setPrevNudgeId(recommendations.nudge_id);

			const nudgeId = `${activeNudge.title} - ${activeNudge.id}`;
			fireRovoButtonUIEvent({
				source: surfaceMetadata.contentType,
				action: 'rendered',
				actionSubject: 'rovoButton',
				actionSubjectId: 'aiRovoProactiveNudge',
				attributes: {
					pageMode: surfaceMetadata.pageMode,
					id: activeNudge.id,
					nudgeId,
				},
				product: surfaceMetadata.product,
			});
			window.requestAnimationFrame(() => {
				/**
				 * Needs to be set to visible in next tick, otherwise
				 * it will not animate
				 */
				setIsNudgeVisible(true);
			});
		}
	}, [activeNudge, fireRovoButtonUIEvent, prevNudgeId, recommendations, surfaceMetadata]);

	const onDismissClick = useCallback(() => {
		if (activeNudge && recommendations) {
			setDismissedIds((prevIds) => ({ ...prevIds, [recommendations.nudge_id]: true }));

			const nudgeId = `${activeNudge.title} - ${activeNudge.id}`;
			fireRovoButtonUIEvent({
				source: surfaceMetadata.contentType,
				action: 'dismissed',
				actionSubject: 'rovoButton',
				actionSubjectId: 'aiRovoProactiveNudge',
				attributes: {
					pageMode: surfaceMetadata.pageMode,
					id: activeNudge.id,
					dismissedMethod: 'manual',
					nudgeId,
				},
				product: surfaceMetadata.product,
			});
		}
	}, [
		activeNudge,
		fireRovoButtonUIEvent,
		recommendations,
		surfaceMetadata.contentType,
		surfaceMetadata.pageMode,
		surfaceMetadata.product,
	]);

	const onNudgeClick = useCallback(() => {
		if (activeNudge && recommendations) {
			setAcceptedIds((prevIds) => ({ ...prevIds, [recommendations.nudge_id]: true }));

			if (activeNudge.viewConfig) {
				changeView(activeNudge);
			}
			activeNudge.onClick?.();

			const nudgeId = `${activeNudge.title} - ${activeNudge.id}`;
			fireRovoButtonUIEvent({
				source: surfaceMetadata.contentType,
				action: 'clicked',
				actionSubject: 'rovoButton',
				actionSubjectId: 'aiRovoProactiveNudge',
				attributes: {
					pageMode: surfaceMetadata.pageMode,
					id: activeNudge.id,
					eventNudgeId: nudgeId,
				},
				product: surfaceMetadata.product,
			});
		}
	}, [
		activeNudge,
		changeView,
		fireRovoButtonUIEvent,
		recommendations,
		surfaceMetadata.contentType,
		surfaceMetadata.pageMode,
		surfaceMetadata.product,
	]);

	const onWrapperMouseEnter = useCallback(() => {
		setIsMouseHovering(true);

		hoverTimerRef.current = setTimeout(() => {
			const nudgeId = activeNudge ? `${activeNudge.title} - ${activeNudge.id}` : '';
			fireRovoButtonUIEvent({
				source: surfaceMetadata.contentType,
				action: 'hovered',
				actionSubject: 'rovoButton',
				actionSubjectId: 'aiRovoProactiveNudge',
				attributes: {
					product: surfaceMetadata.product,
					productPage: surfaceMetadata.contentType,
					pageMode: surfaceMetadata.pageMode,
					nudgeId,
				},
				product: surfaceMetadata.product,
			});
		}, 1000);
	}, [
		activeNudge,
		fireRovoButtonUIEvent,
		surfaceMetadata.contentType,
		surfaceMetadata.pageMode,
		surfaceMetadata.product,
	]);

	const onWrapperMouseLeave = useCallback(() => {
		setIsMouseHovering(false);

		if (hoverTimerRef.current) {
			clearTimeout(hoverTimerRef.current);
			hoverTimerRef.current = null;
		}
	}, []);

	useEffect(() => {
		let timeoutId: NodeJS.Timeout | undefined;
		let slideoutTimeoutId: NodeJS.Timeout | undefined;
		if (activeNudge && recommendations && !isMouseHovering) {
			timeoutId = setTimeout(() => {
				setDismissedIds((prevIds) => ({ ...prevIds, [recommendations.nudge_id]: true }));
				fireRovoButtonUIEvent({
					source: surfaceMetadata.contentType,
					action: 'dismissed',
					actionSubject: 'rovoButton',
					actionSubjectId: 'aiRovoProactiveNudge',
					attributes: {
						pageMode: surfaceMetadata.pageMode,
						id: activeNudge.id,
						timerDuration: NUDGE_DISMISS_TIMEOUT,
						dismissedMethod: 'timer_expired',
					},
					product: surfaceMetadata.product,
				});
			}, NUDGE_DISMISS_TIMEOUT);

			slideoutTimeoutId = setTimeout(() => {
				setIsNudgeVisible(false);
			}, NUDGE_DISMISS_TIMEOUT - ANIMATION_DURATION);
		}

		/**
		 * On mouse enter, isMouseHovering is set to true, will trigger
		 *  cleanup function to clear timeout.
		 */
		return () => {
			if (timeoutId) {
				clearTimeout(timeoutId);
			}
			if (slideoutTimeoutId) {
				clearTimeout(slideoutTimeoutId);
			}
		};
	}, [
		activeNudge,
		fireRovoButtonUIEvent,
		isMouseHovering,
		recommendations,
		surfaceMetadata.contentType,
		surfaceMetadata.pageMode,
		surfaceMetadata.product,
	]);

	if (!activeNudge) {
		return null;
	}

	return (
		<NudgeContent
			activeNudge={activeNudge}
			isNudgeVisible={isNudgeVisible}
			onWrapperMouseEnter={onWrapperMouseEnter}
			onWrapperMouseLeave={onWrapperMouseLeave}
			onNudgeClick={onNudgeClick}
			onDismissClick={onDismissClick}
		/>
	);
};
